/**
 * ClarityUserProvider.jsx
 *
 * Fires PostHog identity calls once per session when the user is authenticated.
 * Wraps the app in index.js so it runs before any tracked events fire.
 *
 * Uses CompanyService.getMyCompany() — same call App.js makes for the company
 * gate — to get firm name, tier, role, and subscription status.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../auth.js';
import CompanyService from '../services/companyService.js';
import {
  phIdentify,
  phRegister,
  phDailyOpen,
  phReset,
} from '../utils/posthogEvents';

const ClarityUserProvider = ({ children }) => {
  const auth = useAuth();
  const [identified, setIdentified] = useState(false);

  useEffect(() => {
    if (!auth?.user || identified) return;

    const profile = auth.user.profile;
    const userId = profile?.sub;
    if (!userId) return;

    (async () => {
      try {
        // Same call App.js uses for the company gate
        // Returns { company, membership } 
        const payload = await CompanyService.getMyCompany();
        const company    = payload?.company    ?? null;
        const membership = payload?.membership ?? null;

        // 1. Identify the user in PostHog
        phIdentify({
          userId,
          email:              profile?.email,
          name:               profile?.name,
          firmName:           company?.name               ?? null, // Company.name
          tier:               company?.planKey            ?? null, // 'starter' | 'firm'
          role:               membership?.role            ?? null, // 'ADMIN' | 'MEMBER'
          subscriptionStatus: company?.subscriptionStatus ?? null, // 'active' | 'inactive'
        });

        // 2. Set super properties — auto-attach to every event this session.
        // project_count and active_project_id are null here and get updated
        // by phRegister() / phSetActiveProject() once projects load in App.js.
        phRegister({
          tier:            company?.planKey ?? null,
          projectCount:    null,
          activeProjectId: null,
        });

        // 3. Daily active user signal — fires at most once per calendar day
        phDailyOpen({
          companyCreatedAt: company?.createdAt ?? null,
        });

        setIdentified(true);
      } catch (err) {
        // Analytics failure should never surface to the user
        console.warn('[PostHog] Identity setup failed:', err);
      }
    })();
  }, [auth, identified]);

  // Reset PostHog when user signs out
  useEffect(() => {
    if (!auth?.user && identified) {
      phReset();
      setIdentified(false);
    }
  }, [auth?.user, identified]);

  return children;
};

export default ClarityUserProvider;